import React, {
  useState,
  useEffect,
  useCallback
} from "react";
import {
  getFeeSettings,
  updateFeeSettings,
  getFeeSettingsPortal,
  updateFeeSettingsPortal,
  uploadReceiptLogo,
  uploadReceiptLogoPortal,
  deleteReceiptLogo,
  deleteReceiptLogoPortal,
  uploadReceiptSignature,
  uploadReceiptSignaturePortal,
  deleteReceiptSignature,
  deleteReceiptSignaturePortal
} from "../../api/fees/feeApi";
import WhatsAppQueueDashboard from "../../components/whatsapp/WhatsAppQueueDashboard";
import NotificationTemplateManager from "../../components/whatsapp/NotificationTemplateManager";
import {
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";
// If you have a device-pairing page from Phase 1 (e.g. OwnerWhatsAppPage), import it here too:
// import OwnerWhatsAppPage from "../whatsapp/OwnerWhatsAppPage";

const TABS = [{
    key: "settings",
    label: "Settings"
  }, {
    key: "template",
    label: "Receipt Template"
  }, {
    key: "queue",
    label: "Message Queue"
  }, {
    key: "templates",
    label: "Templates"
  },
  // { key: "session", label: "Session Setup" }, // uncomment if wiring in the Phase 1 pairing page
];

const FeeSettingsPage = ({
  isPortal = false
}) => {
  const [activeTab, setActiveTab] = useState("settings");
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const toast = useToast();
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal ? await getFeeSettingsPortal() : await getFeeSettings();
      setForm(res.data || res || {});
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load settings");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setForm((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  // Receipt template fields live under form.receiptTemplate — keep them nested
  // so the payload matches what the backend's allow-list expects.
  const handleTemplateChange = (e) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setForm((p) => ({
      ...p,
      receiptTemplate: {
        ...(p.receiptTemplate || {}),
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        defaultDueDateDay: Number(form.defaultDueDateDay),
        receiptNumberPadding: Number(form.receiptNumberPadding),
      };
      if (isPortal) await updateFeeSettingsPortal(payload);
      else await updateFeeSettings(payload);
      toast.success("Settings saved");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fn = isPortal ? uploadReceiptLogoPortal : uploadReceiptLogo;
      const res = await fn(file);
      setForm(res.data || res);
      toast.success("Logo updated");
    } catch (err) {
      toast.error(err.response?.data?.message || "Logo upload failed");
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };
  const handleLogoRemove = async () => {
    setLogoUploading(true);
    try {
      const fn = isPortal ? deleteReceiptLogoPortal : deleteReceiptLogo;
      const res = await fn();
      setForm(res.data || res);
      toast.success("Logo removed");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove logo");
    } finally {
      setLogoUploading(false);
    }
  };
  const handleSignatureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSignatureUploading(true);
    try {
      const fn = isPortal ? uploadReceiptSignaturePortal : uploadReceiptSignature;
      const res = await fn(file);
      setForm(res.data || res);
      toast.success("Signature updated");
    } catch (err) {
      toast.error(err.response?.data?.message || "Signature upload failed");
    } finally {
      setSignatureUploading(false);
      e.target.value = "";
    }
  };
  const handleSignatureRemove = async () => {
    setSignatureUploading(true);
    try {
      const fn = isPortal ? deleteReceiptSignaturePortal : deleteReceiptSignature;
      const res = await fn();
      setForm(res.data || res);
      toast.success("Signature removed");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove signature");
    } finally {
      setSignatureUploading(false);
    }
  };
  const check = (name, label) => (<label className="flex items-center gap-2 text-sm"> <input type="checkbox" name={name} checked={!!form?.[name]} onChange={handleChange} /> {label} </label>);
  const templateCheck = (name, label) => (<label className="flex items-center gap-2 text-sm"> <input type="checkbox" name={name} checked={form?.receiptTemplate?.[name] !== false} onChange={handleTemplateChange} /> {label} </label>);
  return (
    <div className="p-6 max-w-4xl mx-auto"> 
<h1 className="text-2xl font-semibold mb-1">Fee Settings
</h1> 
<p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">School-configurable fee behaviour & notifications
</p> 
<div className="flex gap-1 border-b mb-6">
{TABS.map((tab) => ( 
<button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${ activeTab === tab.key ? "border-blue-600 text-primary-900" : "border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200" }`} >
{tab.label}
</button> ))}
</div>
{activeTab === "settings" && ( <>
{loading || !form ? ( 
<div className="space-y-4 p-6"> 
<Skeleton height={20} width="30%" />
 
<Skeleton height={40} width="100%" />
 
<Skeleton height={40} width="100%" />
 
<Skeleton height={40} width="80%" />
 
</div> ) : ( 
<div className="max-w-3xl"> 
<form onSubmit={handleSubmit} className="bg-white dark:bg-neutral-900 border rounded-card p-6 space-y-6"> 
<section className="space-y-3"> 
<h2 className="font-semibold text-neutral-800 dark:text-neutral-100">Due date
</h2> 
<div> 
<label className="block text-sm mb-1">Default due date day
</label> 
<input type="number" min="1" max="31" name="defaultDueDateDay" value={form.defaultDueDateDay ?? 10} onChange={handleChange} className="w-full border rounded px-3 py-2 text-sm" />
 
</div> 
</section> 
<section className="space-y-3"> 
<h2 className="font-semibold text-neutral-800 dark:text-neutral-100">Receipt
</h2> 
<div className="grid grid-cols-2 gap-3"> 
<div> 
<label className="block text-sm mb-1">Prefix
</label> 
<input name="receiptPrefix" value={form.receiptPrefix || ""} onChange={handleChange} className="w-full border rounded px-3 py-2 text-sm" />
 
</div> 
<div> 
<label className="block text-sm mb-1">Number padding
</label> 
<input type="number" min="3" max="10" name="receiptNumberPadding" value={form.receiptNumberPadding ?? 5} onChange={handleChange} className="w-full border rounded px-3 py-2 text-sm" />
 
</div> 
</div> 
<div> 
<label className="block text-sm mb-1">Currency symbol
</label> 
<input name="currencySymbol" value={form.currencySymbol || "₹"} onChange={handleChange} className="w-full border rounded px-3 py-2 text-sm" />
 
</div> 
<p className="text-xs text-neutral-400 dark:text-neutral-500"> Logo, colors, and layout are now managed under the 
<strong>Receipt Template
</strong> tab. 
</p> 
</section> 
<section className="space-y-3"> 
<h2 className="font-semibold text-neutral-800 dark:text-neutral-100">Notifications
</h2>
{check("whatsappEnabled", "WhatsApp enabled")} {check("smsEnabled", "SMS enabled")} {check("emailEnabled", "Email enabled")}
</section> 
<section className="space-y-3"> 
<h2 className="font-semibold text-neutral-800 dark:text-neutral-100">Payments & transport
</h2>
{check("allowPartialPayments", "Allow partial payments")} {check("allowOverpayment", "Allow overpayment")} {check("transportModuleIntegrationEnabled", "Transport module integration (fetch fee by student later)")}
</section> 
<div className="pt-2"> 
<button type="submit" disabled={saving} className="px-4 py-2 bg-primary-900 text-white rounded text-sm disabled:opacity-50">
{saving ? "Saving..." : "Save settings"}
</button> 
</div> 
</form> 
</div> )}
</>
 )} {activeTab === "template" && ( <>
{loading || !form ? ( 
<div className="space-y-4 p-6"> 
<Skeleton height={20} width="30%" />
 
<Skeleton height={40} width="100%" />
 
<Skeleton height={40} width="100%" />
 
<Skeleton height={40} width="80%" />
 
</div> ) : ( 
<div className="max-w-3xl"> 
<div className="bg-white dark:bg-neutral-900 border rounded-card p-6 space-y-6 mb-6"> 
<section className="space-y-3"> 
<h2 className="font-semibold text-neutral-800 dark:text-neutral-100">Logo
</h2> 
<p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Appears at the top-left of the printed / PDF receipt. JPG, PNG or WEBP, up to 2MB.
</p> 
<div className="flex items-center gap-4">
{form.receiptTemplate?.logoUrl ? ( 
<img src={form.receiptTemplate.logoUrl} alt="Receipt logo" className="h-16 w-16 object-contain border rounded" />
 ) : ( 
<div className="h-16 w-16 border rounded flex items-center justify-center text-xs text-neutral-400 dark:text-neutral-500">No logo
</div> )}
<div className="flex flex-col gap-2"> 
<label className="px-3 py-1.5 text-sm border rounded cursor-pointer w-fit">
{logoUploading ? "Uploading..." : "Upload logo"}
<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleLogoUpload} disabled={logoUploading} />
 
</label>
{form.receiptTemplate?.logoUrl && ( 
<button type="button" onClick={handleLogoRemove} disabled={logoUploading} className="text-xs text-danger-500 text-left disabled:opacity-50"> Remove logo 
</button> )}
</div> 
</div> 
</section> 
<section className="space-y-3"> 
<h2 className="font-semibold text-neutral-800 dark:text-neutral-100">Signature
</h2> 
<p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Optional authorized-signatory image shown at the bottom of the receipt.
</p> 
<div className="flex items-center gap-4">
{form.receiptTemplate?.signatureImageUrl ? ( 
<img src={form.receiptTemplate.signatureImageUrl} alt="Signature" className="h-12 w-24 object-contain border rounded" />
 ) : ( 
<div className="h-12 w-24 border rounded flex items-center justify-center text-xs text-neutral-400 dark:text-neutral-500">No signature
</div> )}
<div className="flex flex-col gap-2"> 
<label className="px-3 py-1.5 text-sm border rounded cursor-pointer w-fit">
{signatureUploading ? "Uploading..." : "Upload signature"}
<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleSignatureUpload} disabled={signatureUploading} />
 
</label>
{form.receiptTemplate?.signatureImageUrl && ( 
<button type="button" onClick={handleSignatureRemove} disabled={signatureUploading} className="text-xs text-danger-500 text-left disabled:opacity-50"> Remove signature 
</button> )}
</div> 
</div> 
<div> 
<label className="block text-sm mb-1">Signature label
</label> 
<input name="signatureLabel" value={form.receiptTemplate?.signatureLabel ?? "Authorized Signatory"} onChange={handleTemplateChange} className="w-full border rounded px-3 py-2 text-sm" />
 
</div> 
</section> 
</div> 
<form onSubmit={handleSubmit} className="bg-white dark:bg-neutral-900 border rounded-card p-6 space-y-6"> 
<section className="space-y-3"> 
<h2 className="font-semibold text-neutral-800 dark:text-neutral-100">Appearance
</h2> 
<div className="grid grid-cols-2 gap-3"> 
<div> 
<label className="block text-sm mb-1">Accent color
</label> 
<div className="flex items-center gap-2"> 
<input type="color" name="accentColor" value={form.receiptTemplate?.accentColor || "#2c3e50"} onChange={handleTemplateChange} className="h-9 w-12 border rounded cursor-pointer" />
 
<input name="accentColor" value={form.receiptTemplate?.accentColor || "#2c3e50"} onChange={handleTemplateChange} className="flex-1 border rounded px-3 py-2 text-sm" />
 
</div> 
</div> 
<div> 
<label className="block text-sm mb-1">Paper size
</label> 
<select name="paperSize" value={form.receiptTemplate?.paperSize || "A4"} onChange={handleTemplateChange} className="w-full border rounded px-3 py-2 text-sm" > 
<option value="A4">A4
</option> 
<option value="A5">A5
</option> 
</select> 
</div> 
</div> 
<div> 
<label className="block text-sm mb-1">Footer text
</label> 
<textarea name="footerText" rows={2} maxLength={300} value={form.receiptTemplate?.footerText ?? "This is a computer-generated receipt."} onChange={handleTemplateChange} className="w-full border rounded px-3 py-2 text-sm" />
 
</div> 
</section> 
<section className="space-y-3"> 
<h2 className="font-semibold text-neutral-800 dark:text-neutral-100">Fields shown on receipt
</h2>
{templateCheck("showFatherName", "Father's name")}
                  {templateCheck("showRollNumber", "Roll number")}
                  {templateCheck("showTransactionId", "Transaction ID")}
                  {templateCheck("showRemarks", "Remarks")}
                </section>

                <div className="pt-2">
                  <button type="submit" disabled={saving} className="px-4 py-2 bg-primary-900 text-white rounded text-sm disabled:opacity-50">
                    {saving ? "Saving..." : "Save template settings"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}

      {activeTab === "queue" && <WhatsAppQueueDashboard isPortal={isPortal} />}

      {activeTab === "templates" && <NotificationTemplateManager isPortal={isPortal} />}
    </div>);
};
export default FeeSettingsPage;
